import React from 'react'
import { ArrowDown2 } from 'iconsax-react'
import { useAppSelector } from '@SonarCX/hooks/redux'

import ButtonMenu from '@SonarCX/components/molecules/ButtonMenu2'
import Info from '@SonarCX/components/atoms/Info'

import { ObjectType, nameMap } from '../types'
import { selectUserType } from '@SonarCX/slices/userType'
import { selectTaskType } from '@SonarCX/slices/taskType'
import { sortByName } from '@SonarCX/utilities'
import useStyles from './styles'

type TypeSelectProps = {
  title: string
  tab: ObjectType
  type: string
  setType: React.Dispatch<React.SetStateAction<string>>
}

const TypeSelect: React.FC<TypeSelectProps> = ({
  title,
  tab,
  type,
  setType
}) => {
  const { types: userTypes } = useAppSelector(selectUserType)
  const { typesValues: taskTypes } = useAppSelector(selectTaskType)

  const typeOptions = React.useMemo(() => {
    const list = tab === ObjectType.Task ? taskTypes : userTypes
    return [
      'null',
      ...list
        .slice()
        .sort(sortByName)
        .map(item => item.id)
    ]
  }, [tab, taskTypes, userTypes])

  const classes = useStyles()

  // Ensure valid types
  React.useEffect(() => {
    const list = tab === ObjectType.Task ? taskTypes : userTypes
    const ids = ['null', ...list.map(item => item.id)]

    if (ids.length === 0) {
      setType('null')
      return
    }

    if (!ids.includes(type)) {
      setType(ids[0])
    }
  }, [tab, taskTypes, userTypes, type, setType])

  // Convert selected type id to name
  const renderOption = React.useCallback(
    (opt: string) => {
      if (opt === 'null') {
        return `All ${nameMap[tab].slice(0, -1)} Types`
      }

      const list =
        tab === ObjectType.Task ? taskTypes : (userTypes as APIBaseType[])
      return list.find(item => item.id === opt)?.name ?? 'Unknown Type'
    },
    [tab, taskTypes, userTypes]
  )

  return (
    <>
      <ButtonMenu
        title={`${nameMap[tab]} Type`}
        options={typeOptions}
        onSelect={type => setType(type)}
        renderOption={renderOption}
        className={classes.root}
        menuProps={{
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
          transformOrigin: { horizontal: 'center', vertical: 'top' }
        }}
      >
        {renderOption(type)}
        <ArrowDown2 className={classes.icon} />
      </ButtonMenu>

      <Info
        text={`These ${title} will show ${
          type === 'null'
            ? `across all ${nameMap[tab]
                .toLocaleLowerCase()
                .slice(0, -1)} pages`
            : `on ${nameMap[
                tab
              ].toLocaleLowerCase()} with the type ${renderOption(type)}`
        }`}
        className={classes.info}
      />
    </>
  )
}

export default TypeSelect
